import React from 'react';
import { Box, Flex, Grid, Text } from 'core/components';
import { getMetricsForTest } from '@kentik/ui-shared/synthetics/healthMetricMapping';
import { getAllHealthThresholds } from '@kentik/ui-shared/synthetics/formatThreshold';

const GROUP_LABELS = {
  ALL_AGENTS: 'all agents',
  LABEL: 'label',
  SITE: 'site'
};

/**
 * Renders health threshold rows for all metrics on a test.
 * @param {{ healthSettings: object, tasks: string[], testType: string }} props
 */
export function HealthThresholdsList({ healthSettings, tasks, testType }) {
  if (!healthSettings || healthSettings.healthDisabled) {
    return null;
  }

  const metricDescriptors = getMetricsForTest(tasks, testType);
  const allThresholds = getAllHealthThresholds(healthSettings, metricDescriptors);

  if (allThresholds.length === 0) {
    return null;
  }

  return (
    <>
      {allThresholds.map(({ label, thresholds, isDisabled }) => {
        if (!thresholds) {
          return null;
        }

        const { warningDisplay, criticalDisplay, isInverted, isStdDev } = thresholds;
        const symbol = isInverted ? '\u2264' : '\u2265';

        return (
          <Box key={label} opacity={isDisabled ? 0.4 : 1}>
            <Text as="div" fontWeight="bold" mb="2px">
              {label}
              {isDisabled && (
                <Text muted fontWeight="normal">
                  {' '}
                  (disabled)
                </Text>
              )}
              {isStdDev && (
                <Text muted fontWeight="normal">
                  {' '}
                  (stddev)
                </Text>
              )}
            </Text>
            <Grid gridTemplateColumns="1fr 1fr" gridGap="2px 8px" ml={1}>
              <Flex alignItems="center" gap="4px">
                <Box width={8} height={8} borderRadius="50%" bg="warning" flexShrink={0} />
                <Text muted>Warning</Text>
              </Flex>
              <Text>
                {symbol} {warningDisplay}
              </Text>
              <Flex alignItems="center" gap="4px">
                <Box width={8} height={8} borderRadius="50%" bg="danger" flexShrink={0} />
                <Text muted>Critical</Text>
              </Flex>
              <Text>
                {symbol} {criticalDisplay}
              </Text>
            </Grid>
          </Box>
        );
      })}
    </>
  );
}

/**
 * Renders grouped alerting summary (percentage thresholds + overrides).
 * @param {{ alerting: object }} props
 */
export function GroupedAlertingSummary({ alerting }) {
  if (!alerting || alerting.alertingType !== 'grouped') {
    return null;
  }

  const settings = alerting.groupedAlertSettings || {};
  const defaults = settings.default || {};
  const overrides = settings.overrides || [];

  const groupBy = defaults.srcGroupBy || 'ALL_AGENTS';
  const groupLabel = GROUP_LABELS[groupBy] || groupBy;
  const pct = defaults.percentOfSrcGroup;
  const groupPhrase = groupBy === 'ALL_AGENTS' ? groupLabel : `agents per ${groupLabel}`;

  return (
    <>
      <Text muted>
        Threshold must occur on at least <Text fontWeight="bold">{pct}%</Text> of {groupPhrase}
      </Text>
      {overrides
        .filter((o) => !o.disabled)
        .map((override) => {
          const overrideGroup = override.srcGroupBy || groupBy;
          const overrideGroupLabel = GROUP_LABELS[overrideGroup] || overrideGroup;
          const overridePhrase =
            overrideGroup === 'ALL_AGENTS' ? overrideGroupLabel : `agents per ${overrideGroupLabel}`;

          return (
            <Text key={override.metric} muted>
              <Text fontWeight="bold">{override.metric}</Text>: {override.percentOfSrcGroup}% of {overridePhrase}
            </Text>
          );
        })}
    </>
  );
}

/**
 * Renders alert window activation/grace period text.
 * @param {{ activate: object }} props
 */
export function AlertWindowSummary({ activate }) {
  if (!activate) {
    return null;
  }

  const { times, timeWindow, timeUnit, gracePeriod } = activate;

  if (!times && !timeWindow) {
    return null;
  }

  return (
    <>
      {times && timeWindow && (
        <Text muted>
          Health thresholds must be crossed <Text fontWeight="bold">{times}</Text> time{times > 1 ? 's' : ''} in{' '}
          <Text fontWeight="bold">
            {timeWindow} {timeUnit || 'min'}
          </Text>
        </Text>
      )}
      {gracePeriod && (
        <Text muted>
          Counter resets after <Text fontWeight="bold">{gracePeriod} min</Text>
        </Text>
      )}
    </>
  );
}
